import React, { useState } from 'react';
import { Link } from 'gatsby';
import { useTheme } from '@emotion/react';
import APP_URLS from 'consts/appUrls';
import ECOMMERCE_URLS from 'consts/eCommerceUrls';
import { PAGE_URLS } from 'consts/pageUrls';
import { EXTERNAL_URLS } from 'consts/externalUrls';
import { MODULES } from 'consts/modules';
import { FinancialAssistanceModal } from './components/FinancialAssistanceModal';
import { navExternalWithQueryString } from 'utils/marketingSafeDomain';
import {
  trackSignupCTAClick,
  trackECommerceCTAClick,
} from 'utils/metricsTracking';

import { ReactComponent as Stamp } from './images/curology_logo_stamp.svg';
import { FollowCurology } from './components/FollowCurology';
import {
  BottomBox,
  CopyrightText,
  EfficacyBox,
  DisclaimerBox,
  InnerBox,
  legalLink,
  LegalLinks,
  LinksFlexbox,
  MainBox,
  mainLink,
  MainLinksColumn,
  RightImageWrapper,
  rightImageStyles,
  SocialLinksBox,
  TopLinksbox,
} from './Footer.style';
import { LOCAL_COLORS } from 'styles/constants';

export interface FooterProps {
  efficacyFootnote?: boolean;
  isMarketingSafe?: boolean;
  disclaimer?: string | JSX.Element;
  backgroundColor?: string;
}

export const Footer = ({
  efficacyFootnote = false,
  isMarketingSafe = false,
  disclaimer,
  backgroundColor = LOCAL_COLORS.surfaceDarkBold,
}: FooterProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const theme = useTheme();
  const shouldRenderSiteLinks = !isMarketingSafe;

  const handleEcommerceClick =
    (href: string) => (event: React.SyntheticEvent) => {
      event.preventDefault();
      trackECommerceCTAClick(MODULES.footer);
      navExternalWithQueryString(href);
    };

  return (
    <React.Fragment>
      {modalIsOpen && (
        <FinancialAssistanceModal onClose={() => setModalIsOpen(false)} />
      )}
      <MainBox backgroundColor={backgroundColor}>
        <InnerBox>
          <TopLinksbox>
            {shouldRenderSiteLinks && (
              <React.Fragment>
                <LinksFlexbox>
                  <MainLinksColumn>
                    <a
                      href={APP_URLS.welcome}
                      css={mainLink}
                      onClick={() => trackSignupCTAClick(MODULES.footer)}
                    >
                      Get Started
                    </a>
                    <a
                      href={ECOMMERCE_URLS.main}
                      css={mainLink}
                      onClick={handleEcommerceClick(ECOMMERCE_URLS.main)}
                    >
                      Shop Products
                    </a>
                    <Link to={PAGE_URLS.whyCurology} css={mainLink}>
                      Why Curology
                    </Link>
                    <Link to={PAGE_URLS.blog.home} css={mainLink(theme)}>
                      Guides
                    </Link>
                    <Link to={PAGE_URLS.ourStory} css={mainLink}>
                      Our Story
                    </Link>
                    <Link to={PAGE_URLS.community} css={mainLink}>
                      Community
                    </Link>
                  </MainLinksColumn>
                  <div>
                    <a href={EXTERNAL_URLS.support} css={mainLink(theme)}>
                      Support
                    </a>
                    <Link to={PAGE_URLS.blog.home} css={mainLink(theme)}>
                      Blog
                    </Link>
                    <Link to={PAGE_URLS.reviews} css={mainLink(theme)}>
                      Reviews
                    </Link>
                    <Link to={PAGE_URLS.careers} css={mainLink(theme)}>
                      Careers
                    </Link>
                    <Link to={PAGE_URLS.contactUs} css={mainLink(theme)}>
                      Contact Us
                    </Link>
                  </div>
                  <SocialLinksBox>
                    <FollowCurology />
                  </SocialLinksBox>
                </LinksFlexbox>
                <RightImageWrapper backgroundColor={backgroundColor}>
                  <Link to={PAGE_URLS.homepage} aria-label="Back to Homepage">
                    <Stamp
                      alt="Curology Logo"
                      role="img"
                      width={88}
                      height={88}
                      css={rightImageStyles}
                    />
                  </Link>
                </RightImageWrapper>
              </React.Fragment>
            )}
          </TopLinksbox>

          <BottomBox>
            <LegalLinks>
              <div>
                <CopyrightText>
                  All Rights Reserved 2014-{currentYear} Curology Inc.
                </CopyrightText>
                <Link to={PAGE_URLS.terms} css={legalLink}>
                  Terms of Service
                </Link>
                <Link to={PAGE_URLS.privacy} css={legalLink}>
                  Privacy Notice
                </Link>
              </div>
              <div>
                <Link to={PAGE_URLS.doNotSell} css={legalLink}>
                  Do Not Sell or Share My Personal Information
                </Link>
                <button
                  onClick={() => setModalIsOpen(true)}
                  css={legalLink}
                  type="button"
                >
                  Curology Access Program
                </button>
                <button
                  onClick={() =>
                    window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open')
                  }
                  css={legalLink}
                  type="button"
                >
                  Manage Cookies
                </button>
              </div>
              {disclaimer && <DisclaimerBox>{disclaimer}</DisclaimerBox>}
            </LegalLinks>
            {efficacyFootnote && (
              <EfficacyBox>
                <a
                  href={EXTERNAL_URLS.otcProductsConsumerReport}
                  target="_blank"
                  rel="noreferrer"
                  css={legalLink}
                >
                  1 Consumer Reports 2011
                </a>
                <a
                  href={EXTERNAL_URLS.combinationEfficacy}
                  target="_blank"
                  rel="noreferrer"
                  css={legalLink}
                >
                  2 Journal of the Ind Medical Assc, 2009
                </a>
              </EfficacyBox>
            )}
          </BottomBox>
        </InnerBox>
      </MainBox>
    </React.Fragment>
  );
};
